import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from './AuthContext';

export const CartContext = createContext();

const DELIVERY_FEE_ID  = 1047;
const CART_KEY         = 'lh_cart';
const INACTIVITY_MS    = 10 * 60 * 1000; // 10 minutes

const loadCartFromStorage = () => {
  try {
    if (!localStorage.getItem('token')) return {};
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return {};
    const { cart, ts } = JSON.parse(raw);
    if (Date.now() - ts > INACTIVITY_MS) {
      localStorage.removeItem(CART_KEY);
      return {};
    }
    return cart || {};
  } catch {
    return {};
  }
};

export const CartProvider = ({ children }) => {
  const { deliveryConfig, user, loading } = useContext(AuthContext);
  const [cart, setCart] = useState(loadCartFromStorage);
  const inactivityTimer = useRef(null);

  const isStorePartner = user?.user_type === 'S';
  const minOrder    = isStorePartner ? 1 : parseFloat(deliveryConfig?.min_order_value ?? 299);
  const deliveryFee = isStorePartner ? 0 : parseFloat(deliveryConfig?.delivery_fee    ?? 0);

  // Persist cart and reset inactivity timer on every cart change
  useEffect(() => {
    const token = localStorage.getItem('token');
    const hasRealItems = Object.values(cart).some(i => i.product.id !== DELIVERY_FEE_ID);

    if (!token || !hasRealItems) {
      localStorage.removeItem(CART_KEY);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return;
    }

    localStorage.setItem(CART_KEY, JSON.stringify({ cart, ts: Date.now() }));

    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setCart({});
      localStorage.removeItem(CART_KEY);
    }, INACTIVITY_MS);
  }, [cart]); // eslint-disable-line

  // Clear cart when user logs out (after auth loading completes)
  useEffect(() => {
    if (!loading && !user) {
      setCart({});
      localStorage.removeItem(CART_KEY);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    }
  }, [user, loading]);

  // Clean up timer on unmount
  useEffect(() => () => { if (inactivityTimer.current) clearTimeout(inactivityTimer.current); }, []);

  const addToCart = (product) => {
    setCart(prev => ({
      ...prev,
      [product.id]: {
        product,
        quantity: (prev[product.id]?.quantity || 0) + 1
      }
    }));
  };

  const removeFromCart = (productId) => {
    setCart(prev => {
      const current = prev[productId]?.quantity || 0;
      if (current <= 1) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: { ...prev[productId], quantity: current - 1 } };
    });
  };

  const removeItemCompletely = (productId) => {
    setCart(prev => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
  };

  const clearCart = () => setCart({});

  const getQuantity = (productId) => cart[productId]?.quantity || 0;

  const cartTotalWithoutFee = Object.values(cart).reduce(
    (sum, item) => item.product.id === DELIVERY_FEE_ID
      ? sum
      : sum + (parseFloat(item.product.price) || 0) * item.quantity,
    0
  );

  const cartTotal = Object.values(cart).reduce(
    (sum, item) => sum + (parseFloat(item.product.price) || 0) * item.quantity, 0
  );

  const cartCount = Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);

  const hasItems = Object.values(cart).some(item => item.product.id !== DELIVERY_FEE_ID);

  const belowMinimum = hasItems && cartTotalWithoutFee < minOrder;

  const hasExemptItem = Object.values(cart).some(
    item => item.product.id !== DELIVERY_FEE_ID && item.product.delivery_fee_exempt === 'Y'
  );

  // Auto-manage the delivery fee product whenever cart or config changes
  useEffect(() => {
    if (!hasItems) {
      setCart(prev => {
        if (!prev[DELIVERY_FEE_ID]) return prev;
        const { [DELIVERY_FEE_ID]: _, ...rest } = prev;
        return rest;
      });
      return;
    }

    if (belowMinimum && !hasExemptItem && deliveryFee > 0) {
      setCart(prev => ({
        ...prev,
        [DELIVERY_FEE_ID]: {
          product: { id: DELIVERY_FEE_ID, name: 'Delivery Fee', price: deliveryFee },
          quantity: 1,
        },
      }));
    } else {
      setCart(prev => {
        if (!prev[DELIVERY_FEE_ID]) return prev;
        const { [DELIVERY_FEE_ID]: _, ...rest } = prev;
        return rest;
      });
    }
  }, [belowMinimum, hasExemptItem, deliveryFee, hasItems]); // eslint-disable-line

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      removeItemCompletely,
      clearCart,
      getQuantity,
      cartCount,
      cartTotal,
      cartTotalWithoutFee,
      belowMinimum,
      hasExemptItem,
      minOrder,
      deliveryFee,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
